import styled from "@emotion/styled";
import { useEffect, useRef, useState, useTransition } from "react";
import { EntrySearchResults } from "../search-results-data/entry-search-results/EntrySearchResults";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { LoadMoreButton } from "./LoadMoreButton";
import { SearchResultsProps } from "../search-results-data/search-results/SearchResults";
import { SearchResultsResponse } from "../search-results-data/search-results/types";
import { CustomComponent } from "@kaltura/mediaspace-shared-ui";
import { EntryListSkeleton } from "../search-results-skeleton/EntryListSkeleton";
import { AccumulatedResults } from "@kaltura/mediaspace-shared-search";

export interface SearchResultsWithLoadMoreProps<T extends KalturaESearchEntryResult = KalturaESearchEntryResult>
    extends Omit<SearchResultsProps<T>, "page" | "onPageChange">,
        SearchResultsResponse<T> {
    /**
     * Callback to load more results
     */
    onLoadMore: () => void;
    /**
     * Whether currently loading more results
     */
    isLoadingMore?: boolean;
    /**
     * Function to update the cached results
     * The oldData and return type use AccumulatedResults structure with entryResults
     */
    updateCachedResults?: (updater: (oldData: AccumulatedResults<T>) => AccumulatedResults<T>) => void;
}

const StyledResultsHeader = styled("div")({
    display: "flex",
    justifyContent: "space-between",
});

const LoadMoreSkeletonContainer = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

/**
 * Search Results with Load More button instead of pagination
 */
export function SearchResultsWithLoadMore<T extends KalturaESearchEntryResult = KalturaESearchEntryResult>(
    props: SearchResultsWithLoadMoreProps<T>
) {
    const {
        entryResults,
        onEntryShowMore,
        isWideLayout,
        isTextSearch,
        onInvalidate,
        additionalInvalidation,
        areSearchResultsDraggable = false,
        header,
        mediaCard,
        getDragData,
        onLoadMore,
        isLoadingMore = false,
        updateCachedResults,
    } = props;
    const [activeEntry, setActiveEntry] = useState<string>();
    const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
    const [isPending, startTransition] = useTransition();
    const [hasMoreItems, setHasMoreItems] = useState(true);
    const previousCountRef = useRef<number>(0);
    const firstNewItemIndexRef = useRef<number | null>(null);
    const mediaCardRefs = useRef<Map<number, HTMLElement>>(new Map());

    const handleMore = (searchResult: T, index: number) => {
        setActiveEntry(searchResult.object.id);
        setActiveItemIndex(index);
        onEntryShowMore(searchResult, index);
    };

    const handleInvalidate = () => {
        // reset selections
        setActiveEntry(undefined);
        setActiveItemIndex(null);

        // update container
        onInvalidate?.();
        additionalInvalidation?.();
    };

    const loadMoreAction = () => {
        // Store the current count before loading more
        if (entryResults?.objects) {
            firstNewItemIndexRef.current = entryResults.objects.length;
        }
        onLoadMore();
    };

    const handleLoadMore = () => {
        startTransition(() => {
            loadMoreAction();
        });
    };

    useEffect(() => {
        // when media list updates, reset all local selections
        setActiveEntry(undefined);
        setActiveItemIndex(null);

        // Track if new items were added after load more
        if (entryResults?.objects) {
            const currentCount = entryResults.objects.length;

            // If we were loading more and the count didn't change, no more items available
            if (!isLoadingMore && previousCountRef.current > 0 && currentCount === previousCountRef.current) {
                setHasMoreItems(false);
            }

            // Focus management: After loading more items, focus the first new item
            const shouldFocus =
                !isLoadingMore && firstNewItemIndexRef.current !== null && currentCount > firstNewItemIndexRef.current;

            if (shouldFocus) {
                // Capture the index value to prevent it from being reset
                const indexToFocus = firstNewItemIndexRef.current!;

                // Reset immediately to prevent duplicate focus attempts
                firstNewItemIndexRef.current = null;

                // Use queueMicrotask to defer focus until after the current render cycle completes
                queueMicrotask(() => {
                    const firstNewItemElement = mediaCardRefs.current.get(indexToFocus);

                    if (firstNewItemElement) {
                        // Focus the first interactive element within the media card
                        const focusableElement = firstNewItemElement.querySelector<HTMLElement>(
                            'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
                        );

                        if (focusableElement) {
                            focusableElement.focus();
                        }
                    }
                });
            }

            previousCountRef.current = currentCount;
        }
    }, [entryResults, isLoadingMore]);

    // Reset hasMoreItems and focus tracking when filters change (totalCount changes)
    // But preserve firstNewItemIndexRef if we're in the middle of loading more
    useEffect(() => {
        setHasMoreItems(true);
        previousCountRef.current = 0;
        // Only reset firstNewItemIndexRef if we're not currently loading more
        if (!isLoadingMore) {
            firstNewItemIndexRef.current = null;
        }
    }, [entryResults?.totalCount, isLoadingMore]);

    // Callback to register media card refs for focus management
    const setMediaCardRef = (index: number, element: HTMLElement) => {
        mediaCardRefs.current.set(index, element);
    };

    return (
        <div className="kms-ds-my-media-results">
            {!!header && !!entryResults?.objects.length && (
                <StyledResultsHeader role="region" aria-live="polite">
                    <CustomComponent component={header} onReload={handleInvalidate} entryResults={entryResults} />
                </StyledResultsHeader>
            )}
            {!!entryResults?.objects.length && (
                <EntrySearchResults<T>
                    isTextSearch={isTextSearch}
                    isWideLayout={isWideLayout}
                    entries={entryResults.objects}
                    activeEntry={activeEntry}
                    onMore={handleMore}
                    onInvalidate={handleInvalidate}
                    activeItemIndex={activeItemIndex}
                    areSearchResultsDraggable={areSearchResultsDraggable}
                    mediaCard={mediaCard}
                    getDragData={getDragData}
                    updateCachedResults={updateCachedResults}
                    setMediaCardRef={setMediaCardRef}
                />
            )}

            <>
                {isLoadingMore && (
                    <LoadMoreSkeletonContainer>
                        <EntryListSkeleton count={3} />
                    </LoadMoreSkeletonContainer>
                )}
                <LoadMoreButton
                    isLoading={isLoadingMore || isPending}
                    hasMore={hasMoreItems}
                    onLoadMore={handleLoadMore}
                />
            </>
        </div>
    );
}

export default SearchResultsWithLoadMore;
